(() => {
  const lifecycleKey = 'dessonScrollingTrackLifecycle';
  const selector = '[data-desson-scrolling-track]';
  const existingLifecycle = window[lifecycleKey];
  if (existingLifecycle) {
    existingLifecycle.initialize(document);
    return;
  }
  const controllers = new Set();
  let wheelListening = false;

  class DessonScrollingTrack {
    constructor(root) {
      this.root = root;
      this.lanes = [...root.querySelectorAll('[data-desson-scrolling-lane]')];
      this.rate = 1;
      this.frame = 0;
      this.hovered = false;
      this.focused = false;
      this.visible = false;

      this.onPointerEnter = () => {
        this.hovered = true;
        this.updatePausedState();
      };
      this.onPointerLeave = () => {
        this.hovered = false;
        this.updatePausedState();
      };
      this.onFocusIn = () => {
        this.focused = true;
        this.updatePausedState();
      };
      this.onFocusOut = (event) => {
        this.focused = this.root.contains(event.relatedTarget);
        this.updatePausedState();
      };
    }

    animations() {
      return this.lanes.flatMap((lane) => lane.getAnimations());
    }

    setRate(rate) {
      this.rate = rate;
      this.animations().forEach((animation) => {
        if (rate < 0 && (!animation.currentTime || animation.currentTime <= 0)) {
          const timing = animation.effect?.getComputedTiming?.() || animation.effect?.getTiming?.();
          if (Number.isFinite(timing?.duration) && timing.duration > 0) {
            animation.currentTime = timing.duration;
          }
        }
        animation.playbackRate = rate;
      });
    }

    applyWheel(deltaY) {
      if (!deltaY || !this.visible) return;
      const intensity = Math.min(3, 1.5 + Math.abs(deltaY) / 80);
      this.setRate(deltaY > 0 ? intensity : -intensity);
      this.scheduleDecay();
    }

    updatePausedState() {
      const paused = this.hovered || this.focused;
      this.animations().forEach((animation) => {
        if (paused) animation.pause();
        else animation.play();
      });
    }

    scheduleDecay() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => this.decay());
    }

    decay() {
      this.frame = 0;
      const nextRate = this.rate + (1 - this.rate) * 0.16;
      if (Math.abs(1 - nextRate) < 0.01) {
        this.setRate(1);
        return;
      }
      this.setRate(nextRate);
      this.scheduleDecay();
    }

    start() {
      this.root.addEventListener('pointerenter', this.onPointerEnter);
      this.root.addEventListener('pointerleave', this.onPointerLeave);
      this.root.addEventListener('focusin', this.onFocusIn);
      this.root.addEventListener('focusout', this.onFocusOut);
      controllers.add(this);
      visibilityObserver.observe(this.root);
    }

    destroy() {
      this.root.removeEventListener('pointerenter', this.onPointerEnter);
      this.root.removeEventListener('pointerleave', this.onPointerLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      visibilityObserver.unobserve(this.root);
      controllers.delete(this);
      if (!controllers.size) removeWheelListener();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      delete this.root.dessonScrollingTrack;
    }
  }

  const rootsWithin = (container) => {
    const roots = [];
    if (container.matches?.(selector)) roots.push(container);
    roots.push(...container.querySelectorAll(selector));
    return roots;
  };
  const visibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const controller = entry.target.dessonScrollingTrack;
      if (controller) controller.visible = entry.isIntersecting && entry.intersectionRatio > 0;
    });
  });
  const onWheel = (event) => {
    controllers.forEach((controller) => controller.applyWheel(event.deltaY));
  };
  const addWheelListener = () => {
    if (wheelListening) return;
    window.addEventListener('wheel', onWheel, { passive: true });
    wheelListening = true;
  };
  const removeWheelListener = () => {
    if (!wheelListening) return;
    window.removeEventListener('wheel', onWheel);
    wheelListening = false;
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialize = (container = document) => {
    if (reducedMotion.matches) return;
    rootsWithin(container).forEach((root) => {
      if (root.dessonScrollingTrack) return;
      root.dessonScrollingTrack = new DessonScrollingTrack(root);
      root.dessonScrollingTrack.start();
      addWheelListener();
    });
  };

  const destroyWithin = (container) => {
    rootsWithin(container).forEach((root) => root.dessonScrollingTrack?.destroy());
  };
  const destroyAll = () => {
    [...controllers].forEach((controller) => controller.destroy());
  };
  const onReady = () => initialize();
  const onSectionLoad = (event) => initialize(event.target);
  const onSectionUnload = (event) => destroyWithin(event.target);
  const onReducedMotionChange = (event) => {
    if (event.matches) {
      removeWheelListener();
      destroyAll();
    } else {
      initialize(document);
    }
  };
  const destroy = () => {
    removeWheelListener();
    destroyAll();
    visibilityObserver.disconnect();
    document.removeEventListener('DOMContentLoaded', onReady);
    document.removeEventListener('shopify:section:load', onSectionLoad);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    if (reducedMotion.removeEventListener) {
      reducedMotion.removeEventListener('change', onReducedMotionChange);
    } else {
      reducedMotion.removeListener?.(onReducedMotionChange);
    }
    delete window[lifecycleKey];
  };

  window[lifecycleKey] = { initialize, destroy };
  document.addEventListener('DOMContentLoaded', onReady);
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  if (reducedMotion.addEventListener) {
    reducedMotion.addEventListener('change', onReducedMotionChange);
  } else {
    reducedMotion.addListener?.(onReducedMotionChange);
  }
})();
